import catalogue from './nativeIntake.catalogue.json' with { type: 'json' };

/** Immutable questionnaire versions, independent of the signed PDF revision. */
export const NATIVE_INTAKE_PROFILE_VERSIONS = ['chairshare-native-2026-10-22-v1', 'chairshare-native-2026-10-22-v2', 'chairshare-native-2026-10-23-v3'] as const;
export type NativeIntakeProfileVersion = (typeof NATIVE_INTAKE_PROFILE_VERSIONS)[number];
export const NATIVE_INTAKE_PROFILE_VERSION = NATIVE_INTAKE_PROFILE_VERSIONS[2];
export interface NativeIntakeField {
  key: string;
  sourceQuestionId: string;
  label: string;
  stage: number;
  kind: 'text' | 'email' | 'tel' | 'number' | 'select' | 'multi';
  required: boolean;
  hidden: boolean;
  options: readonly string[];
  numberConstraints?: { min: number; max: number };
  exclusiveOptions?: readonly string[];
}
// Keep the source-matched v1 catalogue intact. The owner-approved v2 revision
// changes only ownership choices; saved v1 requests retain their original list.
const v1Fields: readonly NativeIntakeField[] = catalogue as NativeIntakeField[];
const v2Fields: readonly NativeIntakeField[] = v1Fields.map(field => field.key === 'business.ownershipType'
  ? { ...field, options: ['Sole Proprietor', 'Doctor Owned DSO', 'PE Backed DSO'] } : field);
const countMinimums: Readonly<Record<string, number>> = {
  'people.fullTimeEmployeeCount': 1, 'people.ownerDentistCount': 1,
  'people.dentistCount': 0, 'people.hygienistCount': 0,
  'business.yearsInPractice': 1, 'operations.operatories': 0,
  'locations.reportedLocationCount': 0, 'operations.activePatientCount': 0,
  'people.totalEmployeeCount': 0,
};
const v3Fields: readonly NativeIntakeField[] = v2Fields.map(field => {
  const min = countMinimums[field.key];
  if (min !== undefined) return { ...field, kind: 'number', options: [], numberConstraints: { min, max: Number.MAX_SAFE_INTEGER } };
  if (field.key === 'operations.intraoralScanner') return { ...field, kind: 'multi',
    exclusiveOptions: ["I don't have an intraoral scanner", "I' looking for an intraoral scanner"] };
  return field;
});
const fieldsByVersion: Record<NativeIntakeProfileVersion, readonly NativeIntakeField[]> = {
  'chairshare-native-2026-10-22-v1': v1Fields,
  'chairshare-native-2026-10-22-v2': v2Fields,
  'chairshare-native-2026-10-23-v3': v3Fields,
};
const scannerManufacturers = new Map<string, string>();
const scannerField = v3Fields.find(field => field.key === 'operations.intraoralScanner')!;
for (const value of scannerField.options) {
  if (scannerField.exclusiveOptions?.includes(value)) continue;
  // Preserve the catalogue's manufacturer spelling, including questionable
  // source names. Five literal PANDA choices have no manufacturer delimiter.
  const delimiter = value.indexOf(' -');
  if (delimiter > 0) scannerManufacturers.set(value, value.slice(0, delimiter).trim());
  else if (value.startsWith('PANDA ')) scannerManufacturers.set(value, 'PANDA');
}
/** Exact catalogue models only; callers may retain a separate historical taxonomy. */
export function nativeScannerManufacturer(value: string): string | undefined {
  return scannerManufacturers.get(value);
}
export function isNativeIntakeProfileVersion(value: unknown): value is NativeIntakeProfileVersion {
  return typeof value === 'string' && NATIVE_INTAKE_PROFILE_VERSIONS.some(version => version === value);
}
export function nativeIntakeFields(version: NativeIntakeProfileVersion): readonly NativeIntakeField[] {
  return fieldsByVersion[version];
}
export const NATIVE_INTAKE_FIELDS = nativeIntakeFields(NATIVE_INTAKE_PROFILE_VERSION);
export type NativeIntakeFieldKey = (typeof catalogue)[number]['key'];
export interface NativeIntakeProfile {
  schemaVersion: NativeIntakeProfileVersion;
  answers: Partial<Record<NativeIntakeFieldKey, string | string[]>>;
}

/** Decode only a known structured profile; preserve its pinned version. */
export function readNativeIntakeProfile(value: unknown): NativeIntakeProfile | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;
  const raw = value as Record<string, unknown>;
  if (!isNativeIntakeProfileVersion(raw.schemaVersion) || !raw.answers || typeof raw.answers !== 'object' || Array.isArray(raw.answers)) return;
  const answers: NativeIntakeProfile['answers'] = {};
  for (const field of nativeIntakeFields(raw.schemaVersion)) {
    const answer = (raw.answers as Record<string, unknown>)[field.key];
    if (field.kind === 'multi') {
      if (Array.isArray(answer) && answer.every(item => typeof item === 'string')) answers[field.key] = [...answer];
    } else if (typeof answer === 'string') answers[field.key] = answer;
  }
  return { schemaVersion: raw.schemaVersion, answers };
}
